import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Check, Clock, BookOpen, Star, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.15 } })
};

const tierLabel = { entry: 'Entry Level', mid: 'Most Popular', premium: 'Premium' };
const tierColor = { entry: 'bg-emerald-50 text-emerald-700', mid: 'bg-indigo-50 text-indigo-700', premium: 'bg-amber-50 text-amber-700' };

export default function CoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/courses`).then(res => { setCourses(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleEnroll = async (course) => {
    if (!user || user === false) {
      navigate('/login?redirect=/courses');
      return;
    }
    if (user.purchased_courses?.includes(course.id)) {
      navigate('/dashboard');
      return;
    }
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/payments/stripe/checkout`, {
        course_id: course.id,
        origin_url: origin,
        payment_method: 'stripe'
      }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error('Payment error:', err);
      alert('Payment processing failed. Please try again.');
    }
  };

  return (
    <div data-testid="courses-page">
      {/* Hero */}
      <section className="py-24 sm:py-32 bg-gradient-to-br from-indigo-50 via-white to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <span className="text-xs sm:text-sm tracking-[0.2em] uppercase text-indigo-600 font-semibold">Transformation Programs</span>
            <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1.1] text-slate-900 mt-4 mb-6">
              Choose Your Path to{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-amber-600">Transformation</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              From beginner-friendly courses to premium mentorship, find the perfect program for your journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-slate-500">Loading programs...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {courses.map((course, i) => {
                const purchased = user?.purchased_courses?.includes(course.id);
                return (
                  <motion.div key={course.id} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
                    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col" data-testid={`course-card-${course.id}`}>
                      <div className="relative h-56 overflow-hidden">
                        <img src={course.image} alt={course.title} className="w-full h-full object-cover" />
                        <div className="absolute top-4 left-4">
                          <Badge className={`${tierColor[course.tier]} text-xs font-medium`}>{tierLabel[course.tier]}</Badge>
                        </div>
                      </div>
                      <div className="p-8 flex-1 flex flex-col">
                        <div className="flex items-center gap-4 mb-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{course.duration}</span>
                          <span className="flex items-center gap-1"><BookOpen className="w-4 h-4" />{course.modules} Modules</span>
                        </div>
                        <h3 className="font-serif text-2xl font-semibold text-slate-900 mb-2">{course.title}</h3>
                        <p className="text-sm text-slate-500 mb-1">{course.subtitle}</p>
                        <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3">{course.description}</p>
                        <ul className="space-y-2 mb-6 flex-1">
                          {course.benefits?.slice(0, 4).map((b, bi) => (
                            <li key={bi} className="flex items-start gap-2 text-sm text-slate-600">
                              <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{b}
                            </li>
                          ))}
                        </ul>
                        <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-100">
                          <div>
                            <p className="text-2xl font-bold text-slate-900">${course.price}</p>
                            <p className="text-xs text-slate-500">or Rs.{course.price_inr}</p>
                          </div>
                          <Button
                            onClick={() => handleEnroll(course)}
                            className={`rounded-full gap-2 ${purchased ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-900 hover:bg-slate-800'} text-white`}
                            data-testid={`enroll-btn-${course.id}`}
                          >
                            {purchased ? (
                              <><Check className="w-4 h-4" /> Go to Dashboard</>
                            ) : (
                              <><Zap className="w-4 h-4" /> Enroll Now</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Premium Mentorship CTA */}
      <section className="py-24 sm:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Badge className="bg-amber-500/20 text-amber-400 mb-6">Premium Mentorship</Badge>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mb-6">
              Ready for Deep, Personal Transformation?
            </h2>
            <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
              Work directly with Dr. Dinesh in an intensive 1:1 mentorship program. Limited spots available.
            </p>
            <Link to="/services">
              <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-base gap-2" data-testid="mentorship-cta">
                Learn About 1:1 Coaching <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
